import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ReassignSelect({ assignees, exclude, onPick, testId, placeholder }) {
  return (
    <select value="" onChange={e => { if (e.target.value) onPick(e.target.value); }} className="h-6 text-[10px] border border-slate-200 rounded px-1 bg-white text-slate-600 max-w-[190px]" data-testid={testId}>
      <option value="">{placeholder}</option>
      {assignees.filter(a => a.email.toLowerCase() !== (exclude || '').toLowerCase()).map(a => <option key={a.email} value={a.email}>{a.email}</option>)}
    </select>
  );
}

/** Leaders: every named reviewer with the narratives still awaiting their sign-off; reassign all or one. */
export function ReviewerWorkloadDialog({ open, onOpenChange, assignees = [], onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/api/rfp/response/reviewers/workload`, { withCredentials: true }); setData(r.data); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not load reviewer workload'); setData({ reviewers: [] }); }
  }, []);
  useEffect(() => { if (open) { setData(null); load(); } }, [open, load]);
  const reassign = async (from, to, items) => {
    const what = items ? `"${items[0].title}"` : `all of ${from}'s pending narratives`;
    if (!window.confirm(`Hand ${what} to ${to}?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/rfp/response/reviewers/reassign`,
        { from_reviewer: from, to_reviewer: to, items: (items || []).map(x => ({ project_id: x.project_id, deliverable_id: x.deliverable_id })) }, { withCredentials: true });
      toast.success(`${r.data.moved} document${r.data.moved === 1 ? '' : 's'} reassigned to ${to}`);
      await load(); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not reassign'); }
    finally { setBusy(false); }
  };
  const reviewers = data?.reviewers || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rfp-reviewer-workload-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4 text-[#4ABFCF]" />Reviewer workload — narratives awaiting sign-off</DialogTitle>
        </DialogHeader>
        {!data ? <Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF] mx-auto my-6" /> : !reviewers.length ? (
          <p className="text-xs text-slate-500 py-6 text-center" data-testid="rfp-reviewer-workload-empty">No narrative documents are waiting on a reviewer.</p>
        ) : (
          <div className="space-y-3">
            {reviewers.map(r => (
              <div key={r.reviewer} className="border border-slate-200 rounded-md" data-testid={`rfp-rw-reviewer-${r.reviewer}`}>
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100 flex-wrap">
                  <span className="text-xs font-semibold text-[#0F2040]">{r.reviewer}</span>
                  <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200" data-testid={`rfp-rw-count-${r.reviewer}`}>{r.documents.length} pending</Badge>
                  <span className="ml-auto text-[10px] text-slate-500">Reassign all to</span>
                  <ReassignSelect assignees={assignees} exclude={r.reviewer} placeholder="pick a reviewer…" testId={`rfp-rw-reassign-all-${r.reviewer}`} onPick={to => reassign(r.reviewer, to)} />
                </div>
                <div className="divide-y divide-slate-100">
                  {r.documents.map(d => (
                    <div key={d.deliverable_id} className="flex items-center gap-2 px-3 py-1.5 text-xs flex-wrap" data-testid={`rfp-rw-doc-${d.deliverable_id}`}>
                      <a href={`/rfp-response?project=${d.project_id}`} className="font-medium text-[#0F2040] hover:underline truncate max-w-[260px]">{d.title}</a>
                      <span className="text-[10px] text-slate-500 truncate max-w-[200px]">{d.project}{d.due_date ? ` · due ${d.due_date}` : ''}</span>
                      <span className="text-[10px] text-slate-500">{d.drafted}/{d.sections} drafted</span>
                      {d.gaps > 0 && <span className="text-[10px] text-amber-600 inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />{d.gaps} gap{d.gaps === 1 ? '' : 's'}</span>}
                      {d.ready ? <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />ready to sign</Badge>
                        : <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">not yet complete</Badge>}
                      <span className="ml-auto" />
                      <ReassignSelect assignees={assignees} exclude={r.reviewer} placeholder="reassign to…" testId={`rfp-rw-reassign-${d.deliverable_id}`} onPick={to => reassign(r.reviewer, to, [d])} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {busy && <p className="text-[10px] text-slate-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Reassigning…</p>}
      </DialogContent>
    </Dialog>
  );
}
